/**
 * Local-date helpers.
 *
 * `new Date(...).toISOString().split('T')[0]` is the usual way to get YYYY-MM-DD here,
 * and in India it is wrong in two distinct ways:
 *
 *   new Date().toISOString()            "today". toISOString reports UTC, and IST is
 *                                       UTC+5:30, so between 00:00 and 05:30 local it
 *                                       yields YESTERDAY. A form defaulted at 2am opens
 *                                       on the previous day.
 *
 *   new Date(y, m, 0).toISOString()     a locally-constructed date, e.g. the last day of
 *                                       a month. Local midnight converts back to 18:30 the
 *                                       previous day in UTC, so every month-end became the
 *                                       second-to-last — 30 Sept read as 29 Sept, and
 *                                       February as the 27th. Wrong every time, not just
 *                                       before dawn.
 *
 * These read local calendar parts and never touch UTC.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** YYYY-MM-DD from a Date's local parts. */
export const ymdLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** YYYY-MM-DD for today, in the browser's local timezone. */
export const todayYmd = (): string => ymdLocal(new Date());

/**
 * YYYY-MM-DD from a Date or date-ish string, using local calendar parts.
 *
 * A string already shaped YYYY-MM-DD is returned untouched rather than re-parsed: the
 * Date string parser treats a bare date as UTC midnight, which is the very shift this
 * helper exists to avoid. Returns '' when the value is absent or unparseable, so callers
 * can keep their existing `value ? ... : ''` shape.
 */
export const toYmd = (value: string | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return ymdLocal(d);
};
